import { FC, useMemo } from 'react';
import { DocumentTextIcon, PaperClipIcon } from '@heroicons/react/outline';
import classNames from 'classnames';

import {
  ActivityType,
  ActivityTemplateDataFragment,
} from '../../generated/graphql';
import { SvgIconComponent } from '../types/svgs';

import ActivityAction from '../svgs/ActivityAction';
import ActivityAssessment from '../svgs/ActivityAssessment';
import ActivityAudio from '../svgs/ActivityAudio';
import ActivityForm from '../svgs/ActivityForm';
import ActivityImage from '../svgs/ActivityImage';
import ActivityLink from '../svgs/ActivityLink';
import ActivityPdf from '../svgs/ActivityPdf';
import Signature from '../svgs/Signature';

const pluralize = (count: number, singular: string): string => {
  if (count === 1) return singular;

  if (singular.endsWith('Activity')) {
    return singular.replace(/Activity$/, 'Activities');
  }

  return `${singular}s`;
};

const ACTIVITY_TYPE_CONFIG: Record<
  ActivityType,
  { icon: SvgIconComponent; label: string; bgColor: string }
> = {
  [ActivityType.Action]: {
    icon: ActivityAction,
    label: 'Activity',
    bgColor: 'bg-green-25',
  },
  [ActivityType.Assessment]: {
    icon: ActivityAssessment,
    label: 'Assessment',
    bgColor: 'bg-purple-25',
  },
  [ActivityType.Audio]: {
    icon: ActivityAudio,
    label: 'Audio file',
    bgColor: 'bg-blue-25',
  },
  [ActivityType.Esign]: {
    icon: Signature,
    label: 'E-Sign Document',
    bgColor: 'bg-red-25',
  },
  [ActivityType.Image]: {
    icon: ActivityImage,
    label: 'Image',
    bgColor: 'bg-purple-25',
  },
  [ActivityType.Link]: {
    icon: ActivityLink,
    label: 'Link',
    bgColor: 'bg-orange-25',
  },
  [ActivityType.Pdf]: {
    icon: ActivityPdf,
    label: 'PDF Document',
    bgColor: 'bg-neutral-50',
  },
  [ActivityType.PdfForm]: {
    icon: ActivityForm,
    label: 'PDF Form',
    bgColor: 'bg-red-25',
  },
  [ActivityType.Text]: {
    icon: DocumentTextIcon,
    label: 'Text File',
    bgColor: 'bg-blue-25',
  },
};

export const TotalAttachmentsCountBadge: FC<{
  count: number;
}> = ({ count }) => {
  if (count === 0) return null;

  const attachmentText = pluralize(count, 'Attachment');
  const label = `Includes ${count} ${attachmentText}`;

  return (
    <div
      className="flex items-center gap-x-1 rounded-full bg-blue-25 px-2 py-1"
      aria-label={label}
      title={label}
    >
      <PaperClipIcon className="h-4 w-4 text-neutral-150" />
      <span className="text-small-caption font-medium text-neutral-150">
        {count} {attachmentText}
      </span>
    </div>
  );
};

export const ActivityTypeBadges: FC<{
  activityTemplates: Pick<ActivityTemplateDataFragment, 'activityType'>[];
}> = ({ activityTemplates }) => {
  const resourceCounts = useMemo(() => {
    const counts: Partial<Record<ActivityType, number>> = {};
    activityTemplates.forEach((template) => {
      counts[template.activityType] = (counts[template.activityType] || 0) + 1;
    });
    return counts;
  }, [activityTemplates]);

  if (Object.keys(resourceCounts).length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-y-1.5">
      <div className="text-small-caption font-bold text-neutral-110">
        Attached resources by type:
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {Object.entries(resourceCounts).map(([activityType, count]) => {
          const config = ACTIVITY_TYPE_CONFIG[activityType as ActivityType];
          if (!config || count === 0) return null;

          const IconComponent = config.icon;
          const label = `Includes ${count} ${pluralize(count, config.label)}`;

          return (
            <div
              key={activityType}
              className={classNames(
                'flex items-center gap-x-1 rounded-full px-2 py-1',
                config.bgColor,
              )}
              aria-label={label}
              title={label}
            >
              <IconComponent className="h-4 w-4 text-neutral-150" />
              <span className="text-small-caption font-medium text-neutral-150">
                {count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ActivityTypeBadges;
